import type { ContentCardItem, PolicyEvaluation } from '@content-search/domain';
import type { ClassificationDialogData, ClassificationContentType, ClassificationDialogPolicy, ClassificationDialogUserInfo } from '@core/domain';

/** Map content type string to ClassificationContentType */
const CONTENT_TYPE_MAP: Record<string, ClassificationContentType> = {
  audio: 'audio',
  image: 'image',
  text: 'text',
  mixed: 'mixed',
};

/** Resolve content type from item type or contentType field */
function resolveContentType(item: ContentCardItem): ClassificationContentType {
  const typeKey = item.type?.toLowerCase() || item.contentType?.toLowerCase() || '';

  return CONTENT_TYPE_MAP[typeKey] || 'text';
}

/** Type guard to safely extract content_type from label (may come from API but isn't in current type) */
function getLabelContentType(label: unknown): string | undefined {
  if (typeof label === 'object' && label !== null && 'content_type' in label) {
    return (label as { content_type: string }).content_type;
  }

  return undefined;
}

/**
 * Resolve the content type for a single label.
 * Priority: label's own content_type → label metadata lookup → item content type fallback
 */
function resolveLabelContentType(label: ContentCardItem['labels'][number], itemContentType: string, labelContentTypeMap?: Map<string, string>): string {
  // 1. Label's own content_type from the API
  const apiContentType = getLabelContentType(label);

  if (apiContentType) return apiContentType;

  // 2. Look up from label metadata (by label_id first, then by name)
  if (labelContentTypeMap) {
    if (label.label_id) {
      const byId = labelContentTypeMap.get(label.label_id);

      if (byId) return byId.toLowerCase();
    }

    const byName = labelContentTypeMap.get(label.name.toLowerCase());

    if (byName) return byName.toLowerCase();
  }

  // 3. Fall back to item content type
  return itemContentType;
}

/** Map labels from ContentCardItem format to ClassificationDialogData format */
function mapLabels(item: ContentCardItem, labelContentTypeMap?: Map<string, string>) {
  // Fallback content type from item if label doesn't have its own
  const itemContentType = item.contentType || item.type || 'text';

  return item.labels.map(label => ({
    name: label.name,
    severity: label.severity,
    shortName: label.label_short_name,
    // Resolve label content type with metadata fallback
    contentType: resolveLabelContentType(label, itemContentType, labelContentTypeMap),
    labelId: label.label_id,
    score: label.score,
  }));
}

/** Convert a PolicyEvaluation to ClassificationDialogPolicy format */
function convertPolicyEvaluation(policy: PolicyEvaluation): ClassificationDialogPolicy {
  return {
    id: policy.policyId,
    name: policy.policyName,
    description: policy.reason,
  };
}

/** Convert a ContentSearchPolicy to ClassificationDialogPolicy format with label mappings */
function convertClassificationPolicy(policy: {
  id: string;
  name: string;
  description: string;
  labels: Array<{ labelId: string }>;
}): ClassificationDialogPolicy {
  return {
    id: policy.id,
    name: policy.name,
    description: policy.description,
    labelIds: policy.labels.map(l => l.labelId),
  };
}

/** Map policies from item, returning undefined if empty */
function mapPolicies(items: PolicyEvaluation[], filter: (p: PolicyEvaluation) => boolean) {
  const filtered = items.filter(filter).map(convertPolicyEvaluation);

  return filtered.length > 0 ? filtered : undefined;
}

/** Map user info from item, returning undefined if not present */
function mapUserInfo(item: ContentCardItem): ClassificationDialogUserInfo | undefined {
  if (!item.subjectOwner) return undefined;

  return {
    type: item.subjectOwner.type,
    userId: item.subjectOwner.userId,
    username: item.subjectOwner.username,
  };
}

/** Map classification policies with label mappings, returning undefined if empty */
function mapClassificationPolicies(policies: ContentCardItem['classificationPolicies']): ClassificationDialogPolicy[] | undefined {
  if (!policies || policies.length === 0) return undefined;

  return policies.map(convertClassificationPolicy);
}

/**
 * Converts a ContentCardItem from content-search to the canonical
 * ClassificationDialogData type used by the core ClassificationDialog.
 *
 * @param item The content card item to convert
 * @param labelContentTypeMap Optional map of label ID/name → content type from label definitions.
 *   Used to derive per-label content type when the upstream API doesn't include it.
 */
export function toClassificationDialogData(item: ContentCardItem, labelContentTypeMap?: Map<string, string>): ClassificationDialogData {
  const policyEvaluations = item.policyResult?.policyEvaluations ?? [];

  // Prefer rich classification policies with label mappings when available,
  // fall back to flattened PolicyEvaluation data
  const triggeredPolicies = item.classificationPolicies
    ? mapClassificationPolicies(item.classificationPolicies)
    : mapPolicies(policyEvaluations, p => !p.isAllowed);
  const nonTriggeredPolicies = item.classificationNonTriggeredPolicies
    ? mapClassificationPolicies(item.classificationNonTriggeredPolicies)
    : mapPolicies(policyEvaluations, p => p.isAllowed);

  return {
    id: item.id,
    contentType: resolveContentType(item),
    isSafe: item.classificationResult !== 'rejected',
    text: item.text,
    contentUrl: item.contentUrl || item.audioFileUrl,
    labels: mapLabels(item, labelContentTypeMap),
    confidenceScore: item.classificationMetadata?.confidence_score,
    language: item.language,
    timestamp: item.timestamp,
    duration: item.classificationMetadata?.processing_time_ms,
    triggeredPolicies,
    nonTriggeredPolicies,
    userInfo: mapUserInfo(item),
    topicName: item.topicName || item.topic,
    eventType: item.eventType || 'Classification',
  };
}
